<template>
    <div class="l-meeting-talking-points">
        <!-- Sections -->
        <div v-for="section in sections" :key="section._id">
            <div class="dropzone box box-yellow talking-point-dropzone">
                <!-- Headline -->
                <div
                    v-b-popover.hover.top="{ content: section.name }"
                    class="mouseover-parent tw-truncate tw-font-bold tw-bg-gray-50 tw-p-3 tw-mb-0 tw-rounded-lg"
                    :class="
                        section.newActionItem.type === 'checkinActionItem'
                            ? 'tw-bg-red-50'
                            : 'tw-bg-gray-50'
                    "
                    style="margin-top: -10px"
                >
                    {{ section.name }}
                </div>
                <!-- Headline -->

                <!-- New Talking Point -->
                <div class="tw-flex tw-space-x-4 tw-mb-3">
                    <div class="tw-flex-1">
                        <l-input-text
                            v-model="section.newActionItem.content"
                            type="text"
                            optional
                            hide-help-block
                            size="s"
                            :label="''"
                            :placeholder="
                                section.newActionItem.type == 'checkinTalkingPoint'
                                    ? $t(`meetings.placeholderTalkingPoint`)
                                    : $t(`meetings.placeholderActionItem`)
                            "
                            :input-class="'white-bg'"
                            data-test-id="meeting-edit-new-talking-point"
                            @keyup.enter="createActionItem({ section })"
                        />
                    </div>

                    <span
                        v-show="
                            section.newActionItem.type == 'checkinTalkingPoint' &&
                                section.newActionItem.content.length
                        "
                        class="hidden-xs tw-flex-grow-0 tw-mt-6 talking-point-icon-placeholder tw-text-gray-400 tw-cursor-pointer"
                        @click="
                            () =>
                                toggleActionItemVisibility({
                                    actionItem: section.newActionItem,
                                })
                        "
                        @keyup.enter="
                            () =>
                                toggleActionItemVisibility({
                                    actionItem: section.newActionItem,
                                })
                        "
                    >
                        <i
                            v-b-popover.hover.top="{
                                content:
                                    section.newActionItem.visibility === 'receiver'
                                        ? $t('meetings.labelSharedShort')
                                        : $t('meetings.labelPrivateShort'),
                            }"
                            data-test-class="talking-point-new-eyes"
                            :class="[
                                section.newActionItem.visibility === 'receiver'
                                    ? 'fal fa-eye color-grey'
                                    : 'fal fa-eye-slash',
                            ]"
                        ></i>
                    </span>

                    <span
                        v-show="section.newActionItem.content.length"
                        v-b-popover.hover.top="{ content: $t('meetings.labelEnterToSave') }"
                        data-test-class="talking-point-plus"
                        class="tw-flex-grow-0 tw-mt-6 talking-point-icon-placeholder tw-cursor-pointer"
                        style="margin-left: 12px"
                        @click="() => createActionItem({ section })"
                        @keyup.enter="() => createActionItem({ section })"
                    >
                        <i class="fal fa-level-down fa-rotate-90 color-primary"></i>
                    </span>

                    <span
                        v-show="
                            section.newActionItem.type == 'checkinActionItem' &&
                                section.newActionItem.content.length
                        "
                        class="hidden-xs tw-flex-grow-0 tw-mt-6 talking-point-icon-placeholder tw-text-grey-400 tw-cursor-pointer"
                        @click="
                            () =>
                                toggleActionItemOwner({
                                    actionItem: section.newActionItem,
                                })
                        "
                        @keyup.enter="
                            () =>
                                toggleActionItemOwner({
                                    actionItem: section.newActionItem,
                                })
                        "
                    >
                        <i
                            v-b-popover.hover.top="{ content: $t('meetings.labelReassign') }"
                            data-test-class="talking-point-new-eyes"
                            class="fal fa-repeat color-grey"
                        ></i>
                    </span>

                    <span
                        v-show="section.newActionItem.content.length"
                        class="tw-flex-grow-0 tw-mt-6"
                    >
                        <l-profile-picture
                            :user="section.newActionItem.owners[0]"
                            size="xs"
                            show-popover-name
                        />
                    </span>
                </div>
                <!--/New Talking Point -->

                <!-- Current User Talking Points -->
                <draggable
                    :list="section.itemsCurrentUser"
                    :disabled="editFocused"
                    group="group"
                    @start="dragStart"
                    @end="dragEnd"
                    @change="updateActionItemOrder"
                >
                    <!-- Talking Point -->
                    <div
                        v-for="actionItem in section.itemsCurrentUser"
                        v-show="actionItem.content !== ''"
                        :key="actionItem._id"
                        data-test-class="meeting-edit-talking-point"
                    >
                        <div v-if="!actionItem.editing">
                            <div class="tw-flex tw-space-x-4 tw-mb-1 mouseover-parent tw-group">
                                <!-- Mark Complete -->
                                <span
                                    v-b-popover.hover.right="{
                                        content: $t('meetings.labelMarkComplete'),
                                    }"
                                    data-test-class="meeting-talking-point-checkbox"
                                    class="tw-flex-grow-0 tw-cursor-pointer tw-pl-2"
                                    tabindex="0"
                                    @click="() => toggleActionItemStatus({ actionItem })"
                                    @keyup.enter="() => toggleActionItemStatus({ actionItem })"
                                >
                                    <i
                                        :class="[
                                            actionItem.status === 'done'
                                                ? 'fas fa-check-square color-primary'
                                                : 'fal fa-square color-grey',
                                        ]"
                                        style="width: 12px;"
                                    ></i>
                                </span>
                                <!--/Mark Complete -->

                                <!-- Talking Point Content -->
                                <span
                                    class="tw-flex-1 tw-cursor-pointer"
                                    tabindex="0"
                                    @click="() => toggleActionItemEditing({ actionItem })"
                                    @keyup.enter="() => toggleActionItemEditing({ actionItem })"
                                >
                                    <l-text
                                        :text="actionItem.content"
                                        :class="[
                                            actionItem.status === 'done' &&
                                            actionItem.type == 'checkinActionItem'
                                                ? 'line-through'
                                                : '',
                                        ]"
                                        style="overflow-wrap: break-word;"
                                    />

                                    <time
                                        class="group-hover:tw-opacity-100 tw-block tw-transition-all tw-duration-200 tw-text-gray-400 tw-text-xs tw-leading-none"
                                        :class="{
                                            'tw-opacity-100': actionItem.expanded,
                                            'tw-opacity-0': !actionItem.expanded,
                                        }"
                                        :datetime="actionItem.created_at"
                                        >{{ actionItem.created_at | moment("LL") }}</time
                                    >
                                </span>
                                <!--/Talking Point Content -->

                                <!-- Visibility -->

                                <span
                                    v-if="actionItem.visibility === 'senderOnly'"
                                    v-b-popover.hover.top="{
                                        content: $t('meetings.labelPrivateShort'),
                                    }"
                                    data-test-class="talking-point-hide"
                                    class="hidden-xs tw-flex-grow-0 tw-text-gray-400 mouseover talking-point-icon-placeholder tw-cursor-pointer"
                                    tabindex="0"
                                    @click="() => toggleActionItemVisibility({ actionItem })"
                                    @keyup.enter="() => toggleActionItemVisibility({ actionItem })"
                                >
                                    <i class="fal fa-eye-slash"></i>
                                </span>

                                <span
                                    v-if="actionItem.visibility === 'receiver'"
                                    v-b-popover.hover.top="{
                                        content: $t('meetings.labelSharedShort'),
                                    }"
                                    data-test-class="meeting-talking-point-hide"
                                    class="hidden-xs tw-flex-grow-0 tw-text-gray-400 mouseover talking-point-icon-placeholder tw-cursor-pointer"
                                    tabindex="0"
                                    @click="() => toggleActionItemVisibility({ actionItem })"
                                    @keyup.enter="() => toggleActionItemVisibility({ actionItem })"
                                >
                                    <i class="fal fa-eye"></i>
                                </span>

                                <!-- Visibility -->

                                <!-- Remove -->
                                <span
                                    v-b-popover.hover.top="{
                                        content: $t('meetings.labelRemove'),
                                    }"
                                    data-test-class="talking-point-remove"
                                    class="tw-flex-grow-0 tw-text-gray-400 mouseover talking-point-icon-placeholder tw-cursor-pointer"
                                    tabindex="0"
                                    @click="() => deleteActionItem({ actionItem })"
                                    @keyup.enter="() => deleteActionItem({ actionItem })"
                                >
                                    <i class="fal fa-times"></i>
                                </span>
                                <!--/Remove -->

                                <!-- Toggle Comments -->
                                <span
                                    v-b-popover.hover.top="{
                                        content: $t('meetings.labelComments'),
                                    }"
                                    data-test-class="meeting-talking-poing-comment-toggle"
                                    class="tw-flex-grow-0 clickable talking-point-icon-placeholder tw-cursor-pointer"
                                    tabindex="0"
                                    @click="() => toggleDisplayComments({ actionItem })"
                                    @keyup.enter="() => toggleDisplayComments({ actionItem })"
                                >
                                    <i
                                        class="color-primary tw-cursor-pointer"
                                        :class="[
                                            actionItem.comments && actionItem.comments.length
                                                ? 'fas fa-comment-lines'
                                                : 'fal fa-comment',
                                        ]"
                                    ></i>
                                </span>
                                <!--/Toggle Comments -->

                                <span
                                    v-if="actionItem.type == 'checkinActionItem'"
                                    class="tw-flex-grow-0 talking-point-icon-placeholder tw-cursor-pointer"
                                    @click="
                                        () => {
                                            toggleActionItemOwner({ actionItem });
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            toggleActionItemOwner({ actionItem });
                                        }
                                    "
                                >
                                    <i
                                        v-b-popover.hover.top="{
                                            content: $t('meetings.labelReassign'),
                                        }"
                                        data-test-class="talking-point-new-eyes"
                                        class="fal fa-repeat"
                                        :class="[
                                            isReassigningActionItemOwner !== null &&
                                            isReassigningActionItemOwner === actionItem._id
                                                ? 'color-grey-light'
                                                : 'color-grey',
                                        ]"
                                    ></i>
                                </span>

                                <span class="tw-flex-grow-0">
                                    <l-profile-picture
                                        :user="actionItem.owners[0]"
                                        size="xs"
                                        show-popover-name
                                    />
                                </span>
                            </div>

                            <!-- Talking Points Comments -->
                            <l-meeting-comments
                                v-if="actionItem.expanded"
                                :meeting-id="meeting._id"
                                :action-item="actionItem._id"
                                :comments="actionItem.comments"
                                @update="request => $emit('update', request)"
                            />
                            <!--/Talking Points Comments -->
                        </div>

                        <!-- Edit talking point -->
                        <div v-if="actionItem.editing" class="tw-flex tw-space-x-4 tw-mb-3">
                            <span class="tw-flex-grow-0 talking-point-icon-placeholder"></span>
                            <span class="tw-flex-1">
                                <l-input-text
                                    ref="actionItem"
                                    v-model="actionItem.content"
                                    type="text"
                                    optional
                                    hide-help-block
                                    size="s"
                                    :label="''"
                                    :placeholder="$t(`meetings.placeholderTalkingPoint`)"
                                    :input-class="'white-bg'"
                                    data-test-id="meeting-edit-talkingPoint"
                                    style="margin-top: 0px"
                                    @focus="editFocused = true"
                                    @blur="editFocused = false"
                                    @keyup.enter="
                                        () => {
                                            updateActionItem({ actionItem });
                                            toggleActionItemEditing({ actionItem });
                                        }
                                    "
                                />
                            </span>

                            <span class="tw-flex-grow-0 talking-point-icon-placeholder">
                                <span
                                    v-b-popover.hover.top="{
                                        content: $t('meetings.btnSave'),
                                    }"
                                    class="color-primary clickable"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            updateActionItem({ actionItem });
                                            toggleActionItemEditing({ actionItem });
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            updateActionItem({ actionItem });
                                            toggleActionItemEditing({ actionItem });
                                        }
                                    "
                                >
                                    <i class="fal fa-level-down fa-rotate-90 color-primary"></i>
                                </span>
                            </span>
                            <span class="tw-flex-grow-0">
                                <l-profile-picture
                                    :user="actionItem.owners[0]"
                                    size="xs"
                                    show-popover-name
                                />
                            </span>
                        </div>
                        <!--/Edit talking point -->
                    </div>
                </draggable>
            </div>
            <!-- End Current user's talking points -->

            <!-- Other users' public talking points -->
            <div v-for="actionItem in section.itemsOtherUsers" :key="actionItem._id">
                <div class="tw-flex tw-space-x-4 tw-mb-1 mouseover-parent tw-group">
                    <span
                        v-b-popover.hover.right="{
                            content: $t('meetings.labelMarkComplete'),
                        }"
                        data-test-class="meeting-talking-point-checkbox"
                        class="tw-flex-grow-0 tw-cursor-pointer talking-point-icon-placeholder"
                        tabindex="0"
                        @click="() => toggleActionItemStatus({ actionItem })"
                        @keyup.enter="() => toggleActionItemStatus({ actionItem })"
                    >
                        <i
                            :class="[
                                actionItem.status === 'done'
                                    ? 'fas fa-check-square color-primary'
                                    : 'fal fa-square color-grey',
                            ]"
                            class="tw-ml-2"
                        ></i>
                    </span>

                    <span class="tw-flex-1">
                        <l-text
                            :text="actionItem.content"
                            :class="[
                                actionItem.status === 'done' &&
                                actionItem.type == 'checkinActionItem'
                                    ? 'line-through'
                                    : '',
                            ]"
                            style="overflow-wrap: break-word;"
                        />

                        <time
                            class="group-hover:tw-opacity-100 tw-block tw-transition-all tw-duration-200 tw-text-gray-400 tw-text-xs tw-leading-none"
                            :class="{
                                'tw-opacity-100': actionItem.expanded,
                                'tw-opacity-0': !actionItem.expanded,
                            }"
                            :datetime="actionItem.created_at"
                            >{{ actionItem.created_at | moment("LL") }}</time
                        >
                    </span>

                    <span class="tw-flex-grow-0 talking-point-icon-placeholder"></span>
                    <span class="tw-flex-grow-0 talking-point-icon-placeholder"></span>

                    <!-- Remove -->
                    <span
                        v-if="actionItem.creator == currentUser._id"
                        v-b-popover.hover.top="{
                            content: $t('meetings.labelRemove'),
                        }"
                        data-test-class="talking-point-remove"
                        class="tw-flex-grow-0 talking-point-icon-placeholder mouseover"
                        tabindex="0"
                        @click="() => deleteActionItem({ actionItem })"
                        @keyup.enter="() => deleteActionItem({ actionItem })"
                    >
                        <i class="fal fa-times color-grey "></i>
                    </span>
                    <!--/Remove -->

                    <span
                        v-b-popover.hover.top="{
                            content: $t('meetings.labelComments'),
                        }"
                        data-test-class="meeting-talking-poing-other-comment-toggle"
                        class="tw-flex-grow-0 talking-point-icon-placeholder"
                        @click="() => toggleDisplayComments({ actionItem })"
                        @keyup.enter="() => toggleDisplayComments({ actionItem })"
                    >
                        <i
                            class="color-primary tw-cursor-pointer"
                            :class="{
                                'fas fa-comment-lines': actionItem.comments.length,
                                'fal fa-comment': !actionItem.comments.length,
                            }"
                        ></i>
                    </span>
                    <span
                        v-if="actionItem.type == 'checkinActionItem'"
                        class="tw-flex-grow-0 talking-point-icon-placeholder tw-cursor-pointer"
                        @click="
                            () => {
                                toggleActionItemOwner({ actionItem });
                            }
                        "
                        @keyup.enter="
                            () => {
                                toggleActionItemOwner({ actionItem });
                            }
                        "
                    >
                        <i
                            v-b-popover.hover.top="{
                                content: $t('meetings.labelReassign'),
                            }"
                            data-test-class="talking-point-new-eyes"
                            class="fal fa-repeat"
                            :class="[
                                isReassigningActionItemOwner !== null &&
                                isReassigningActionItemOwner === actionItem._id
                                    ? 'color-grey-light'
                                    : 'color-grey',
                            ]"
                        ></i>
                    </span>

                    <span class="talking-point-picture r-default-margin-s">
                        <l-profile-picture
                            :user="actionItem.owners[0]"
                            size="xs"
                            show-popover-name
                        />
                    </span>
                </div>

                <l-meeting-comments
                    v-if="actionItem.expanded"
                    :meeting-id="meeting._id"
                    :action-item="actionItem._id"
                    :comments="actionItem.comments"
                    @update="request => $emit('update', request)"
                />
            </div>
            <!-- End Other users' public talking points -->
            <br />
        </div>
        <!--/Sections -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import ObjectId from "bson-objectid";

import isEqual from "lodash.isequal";

import Draggable from "vuedraggable";

import LMeetingComments from "@/components/LMeetingComments";
import LProfilePicture from "@/components/LProfilePicture";
import LInputText from "@/components/LInputText";
import LText from "@/components/LText";

import icon from "@/img/icons/meetings.png";

export default {
    components: {
        Draggable,

        LMeetingComments,
        LProfilePicture,
        LInputText,
        LText,
    },
    props: {
        meeting: {
            type: Object,
            required: true,
        },
        meetingModeEnabled: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            sections: [],
            expandedActionItems: [],
            isReassigningActionItemOwner: null,
        };
    },
    computed: {
        ...mapGetters({
            currentUser: "users/current/user",
        }),
    },
    watch: {
        meeting: {
            handler(newVal, oldVal) {
                // In some cases, the prop being passed has the same value as the current one.
                if (isEqual(newVal, oldVal)) {
                    return;
                }

                this.sections = this.parseSections();
            },
        },
        meetingModeEnabled: {
            handler() {
                this.sections = this.parseSections();
            },
        },
    },
    created() {
        this.icon = icon;
        this.sections = this.parseSections();
        this.editFocused = false;
    },
    methods: {
        parseSections() {
            const sections = this.meeting.sections?.map(section => {
                var newActionItem = {
                    showForm: true,
                    status: "open",
                    visibility:
                        this.sections?.find(s => s._id === section._id)?.newActionItem
                            ?.visibility ?? "receiver",
                    skill: section._id,
                    type:
                        section._id == "actionItems" ? "checkinActionItem" : "checkinTalkingPoint",
                    content:
                        this.sections?.find(s => s._id === section._id)?.newActionItem?.content ??
                        "",
                    owners: [
                        this.sections?.find(s => s._id === section._id)?.newActionItem?.owners[0] ??
                            this.currentUser,
                    ],
                };

                // We need to fetch the previous value of the talking point in case the user was editing it
                // while the meeting auto-refreshed

                var itemsCurrentUser = section.itemsCurrentUser
                    .filter(actionItem => actionItem.content !== "")
                    .map(actionItem => {
                        const previousActionItem = this.sections
                            ?.find(s => s._id === section._id)
                            ?.itemsCurrentUser?.find(tp => tp._id === actionItem._id);

                        return {
                            ...actionItem,
                            content: previousActionItem?.editing
                                ? previousActionItem.content
                                : actionItem.content,
                            status: actionItem.status || "",
                            editing: previousActionItem?.editing ?? false,
                            expanded:
                                this.expandedActionItems &&
                                this.expandedActionItems.includes(actionItem._id),
                        };
                    });

                var itemsOtherUsers = section.itemsOtherUsers
                    .filter(actionItem => actionItem.content !== "")
                    .map(actionItem => {
                        return {
                            ...actionItem,
                            expanded:
                                this.expandedActionItems &&
                                this.expandedActionItems.includes(actionItem._id),
                        };
                    });

                // We need to hide private talking points in meeting mode

                itemsCurrentUser = this.meetingModeEnabled
                    ? itemsCurrentUser.filter(x => x.visibility !== "senderOnly")
                    : itemsCurrentUser;

                return {
                    ...section,
                    itemsCurrentUser,
                    itemsOtherUsers,
                    newActionItem,
                };
            });

            return sections;
        },

        toggleTab(tab) {
            this.$emit("tab", tab);
        },
        toggleActionItemEditing({ actionItem }) {
            actionItem.editing = !actionItem.editing;
        },
        toggleDisplayComments({ actionItem }) {
            actionItem.expanded = !actionItem.expanded;

            if (actionItem.expanded) {
                this.expandedActionItems.push(actionItem._id);
            } else {
                const index = this.expandedActionItems.findIndex(tp => tp === actionItem._id);

                if (index > -1) {
                    this.expandedActionItems.splice(index, 1);
                }
            }
        },
        toggleActionItemStatus({ actionItem }) {
            actionItem.status = actionItem.status === "done" ? "" : "done";

            this.updateActionItem({ actionItem });
        },
        toggleActionItemOwner({ actionItem }) {
            if (
                this.isReassigningActionItemOwner &&
                this.isReassigningActionItemOwner !== actionItem._id
            ) {
                return;
            }

            this.isReassigningActionItemOwner = actionItem._id;
            this.updateInProgress(true);

            if (this.timeoutToggleActionItemOwner) {
                clearTimeout(this.timeoutToggleActionItemOwner);
            }
            this.timeoutToggleActionItemOwner = setTimeout(() => {
                // don't save if action item is only a local draft
                if (!actionItem._id) return;

                this.updateActionItem({ actionItem }).finally(() => {
                    this.isReassigningActionItemOwner = null;
                });
            }, 1000);

            var currentIndex = this.meeting.participants.findIndex(
                p => p._id == actionItem.owners[0]._id,
            );
            var newIndex =
                currentIndex + 1 == this.meeting.participants.length ? 0 : currentIndex + 1;

            actionItem.owners = [this.meeting.participants[newIndex]];
        },
        toggleActionItemVisibility({ actionItem }) {
            actionItem.visibility =
                actionItem.visibility === "receiver" ? "senderOnly" : "receiver";

            if (actionItem._id) {
                this.updateActionItem({ actionItem });
            }
        },

        createActionItem({ section }) {
            if (!section?.newActionItem?.content) {
                return;
            }

            const data = {
                ...section.newActionItem,
                _id: ObjectId().toString(),
                checkin: this.meeting._id,
                creator: this.currentUser._id,
            };

            const request = this.$store.dispatch({
                type: "meetings/meetings/createActionItem",
                payload: { data, meetingId: this.meeting._id },
            });

            // We can update before waiting for the request as the update is committed synchronously
            // into the store.
            this.$emit("update", request);

            // Reset new talking point

            section.newActionItem = {
                ...section.newActionItem,
                owners: [this.currentUser],
                showForm: true,
                visibility: "receiver",
                status: "open",
                content: "",
            };
        },

        updateActionItem({ actionItem }) {
            const request = this.$store.dispatch({
                type: "meetings/meetings/updateActionItem",
                payload: { data: { ...actionItem }, meetingId: this.meeting._id },
            });

            // We can update before waiting for the request as the update is committed synchronously
            // into the store.
            this.$emit("update", request);

            return request;
        },

        deleteActionItem({ actionItem }) {
            const request = this.$store.dispatch({
                type: "meetings/meetings/deleteActionItem",
                payload: { data: { ...actionItem }, meetingId: this.meeting._id },
            });

            // We can update before waiting for the request as the update is committed synchronously
            // into the store.
            this.$emit("update", request);
        },
        updateInProgress(updating) {
            // Hold a lock on the store as the current user is dragging a talking point and we want to avoid any updates to the meeting
            // while the current user is re-ordering their talking points
            this.$store.dispatch({
                type: "meetings/meetings/updateInProgress",
                payload: { updating },
            });
        },

        dragStart() {
            this.changeTriggered = false;
            this.updateInProgress(true);
        },
        dragEnd() {
            // If @change was not triggered, we need to revert the lock on the store here
            if (!this.changeTriggered) {
                this.updateInProgress(false);
            }
        },
        updateActionItemOrder(e) {
            // Track if @change was triggered
            this.changeTriggered = true;

            // Moving between two sections triggers two events ("added" and "removed"), which would then create duplicates.
            // As such, we always choose to ignore "removed" events.
            if (e.removed) {
                return;
            }

            // get actionItemIds for current user

            var actionItemIdsOrdered = this.sections
                .map(section => section.itemsCurrentUser.map(tp => ({ ...tp, skill: section._id })))
                .flat();

            var data = {
                actionItemIdsOrdered,
            };

            const request = this.$store.dispatch({
                type: "meetings/meetings/updateActionItemOrder",
                payload: { data, meetingId: this.meeting._id },
            });

            // We can update before waiting for the request as the update is committed synchronously
            // into the store.
            this.$emit("update", request);
        },
    },
};
</script>

<style scoped>
.l-meeting-talking-points >>> .l-text a {
    overflow-wrap: anywhere;
}

.talking-point-input >>> .form-group .form-control {
    margin-bottom: 0;
}
</style>
